import { createFileRoute, Navigate } from '@tanstack/react-router';

import { SettingsAuditTimeline } from '@apps/web/components/settings/settings_audit_timeline';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/settings/change-history')({
    component: ChangeHistoryPage,
});

/**
 * Configuration change history, for Phoebe staff only.
 *
 * The data comes from database triggers, so it covers changes made outside
 * this app entirely — a worker, a script, or someone editing the database by
 * hand. That makes it a support tool rather than a customer feature: it is the
 * fastest way to answer "who turned this off?" while looking at the agency's
 * own settings, instead of switching to the admin panel and finding the org.
 *
 * `internalAdmin` hides the nav entry and this page from agency users. The
 * real enforcement is server-side — the endpoint sits behind the admin router,
 * which requires `User.admin` regardless of which app called it.
 */
function ChangeHistoryPage() {
    const { organizationId, isLoading, gates } = useSettingsGates();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings',
    });

    // Wait for the profile before deciding, so an internal admin on a cold
    // load is not bounced off a deep link while `user.admin` is still
    // resolving.
    if (isLoading) {
        return null;
    }
    if (!gates.internalAdmin) {
        return <Navigate to="/settings" replace={true} />;
    }
    if (organizationId === null) {
        return null;
    }
    return (
        <SettingsPageShell
            title="Change history"
            description="Every configuration change on this organization, including changes made outside the app. Visible to Phoebe staff only."
        >
            <SettingsAuditTimeline
                organizationId={organizationId}
                timezone={organization?.timezone ?? 'UTC'}
            />
        </SettingsPageShell>
    );
}
